import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';

const POSHeldSales: React.FC = () => {
  const [heldSales, setHeldSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchHeldSales = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/pos/sales/held');
      setHeldSales(response || []);
    } catch (error) {
      console.error('Failed to load held sales:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHeldSales();
  }, [fetchHeldSales]);

  const handleResume = async (saleId: number) => {
    try {
      const response = await apiService.post(`/pos/sales/${saleId}/resume`);
      // apiService.post returns response.data already, and the API returns {message, items}
      const resumedData = response.data || response;
      sessionStorage.setItem('pos_resumed_sale', JSON.stringify(resumedData));
      navigate('/pos');
    } catch (error) {
      console.error('Failed to resume sale:', error);
    }
  };

  return (
    <div className="h-full p-4 overflow-y-auto bg-gray-900">
      <h2 className="text-xl font-bold text-white mb-4">Held Sales ({heldSales.length})</h2>

      {loading ? (
        <div className="text-gray-400 text-center py-8">Loading...</div>
      ) : heldSales.length === 0 ? (
        <div className="text-gray-400 text-center py-8">No held sales</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {heldSales.map((sale) => (
            <div key={sale.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-white font-medium">{sale.sale_number}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(sale.held_at).toLocaleString()}
                  </p>
                </div>
                <span className="text-xs px-2 py-1 bg-yellow-900 text-yellow-300 rounded">
                  Held
                </span>
              </div>

              {sale.customer_name && (
                <p className="text-sm text-gray-300 mb-2">Customer: {sale.customer_name}</p>
              )}

              {/* Items preview */}
              <div className="space-y-1 mb-3">
                {sale.items?.slice(0, 3).map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-xs text-gray-400">
                    <span className="truncate flex-1">{item.product_name} x{item.quantity}</span>
                    <span>{Number(item.total).toLocaleString()}</span>
                  </div>
                ))}
                {sale.items?.length > 3 && (
                  <p className="text-xs text-gray-500">+{sale.items.length - 3} more items</p>
                )}
              </div>

              <div className="flex justify-between items-center pt-2 border-t border-gray-700">
                <p className="text-lg font-bold text-green-400">
                  {Number(sale.total_amount).toLocaleString()} MMK
                </p>
                <button
                  onClick={() => handleResume(sale.id)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition"
                >
                  Resume
                </button>
              </div>

              {sale.notes && (
                <p className="text-xs text-gray-500 mt-2 italic">{sale.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default POSHeldSales;
